import React from "react";
import styled from "@emotion/styled";
import { css } from "@emotion/react";

import CenterModal from "@ats/src/components/modals/CenterModal";
import Button from "@ats/src/components/shared/Button";
import FormContainer from "@ats/src/components/forms/FormContainer";
import FormSelect from "@ats/src/components/forms/FormSelect";

import { useToastContext } from "@shared/context/ToastContext";
import { useUpdateCareersPageSubscriptions } from "@shared/queryHooks/admin/useOrganization";
import { adminBooleanOptions } from "@ats/src/lib/lookups";

type Props = {
  organization: any;
  onCancel: () => void;
};

function AdminCareersPageSubscriptionsModal(props: Props) {
  const { organization, onCancel } = props;
  const addToast = useToastContext();
  window.logger("%c[AdminCareersPageSubscriptionsModal] RENDER", "background-color: #8fb4d8", { organization });

  const initialValue =
    organization?.careersPageSubscriptionsEnabled
      && organization.careersPageSubscriptionsEnabled

  const [subscriptionNotificationsEnabled, setSubscriptionNotificationsEnabled] = React.useState(initialValue);
  const [errors, setErrors] = React.useState(null);

  const {
    mutate: updateCareersPageSubscriptions,
    isLoading: isLoadingUpdate,
  } = useUpdateCareersPageSubscriptions();

  const handleOnCancel = (event) => {
    if (event != undefined) {
      event.preventDefault();
    }
    onCancel();
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    setErrors(null);

    updateCareersPageSubscriptions(
      { id: organization.id, subscriptionNotificationsEnabled },
      {
        onSuccess: (data) => {
          window.logger("%c[AdminCareersPageSubscriptionsModal] onSuccess", "color: #19d232", {
            data,
          });
          addToast({
            title: "Careers page subscription notifications updated successfully",
            kind: "success",
            delay: 6000,
          });
          onCancel();
        },
        onError: (error: any) => {
          window.logger("%c[AdminCareersPageSubscriptionsModal] onError", "color: #d28819", {
            error,
          });

          let errorMessage = "An error occurred. Please try again.";

          if (error?.data && error?.data?.errors) {
            const generalErrors = error?.data?.errors?.general;
            if (generalErrors && generalErrors.length > 0) {
              errorMessage = generalErrors[0];
            }
          }
          setErrors([errorMessage]);
        },
      },
    );
  };

  const handleOnInputChange = (name, value) => {
    window.logger("%c[AdminCareersPageSubscriptionsModal] handleOnInputChange", "color: #1976D2", {
      name,
      value,
    });
    setSubscriptionNotificationsEnabled(value);
  };

  const submitButton = (
    <Styled.ButtonContainer>
      <Button type="submit" onClick={handleSubmit} loading={isLoadingUpdate}>
        Update subscriptions
      </Button>
      <Button styleType="secondary" onClick={handleOnCancel}>
        Cancel
      </Button>
    </Styled.ButtonContainer>
  );

  return (
    <CenterModal
      headerTitleText={`Update Careers Page Subscriptions: ${organization.name}`}
      onCancel={handleOnCancel}
    >
      <FormContainer errors={errors} buttons={submitButton} onSubmit={handleSubmit}>
        <FormSelect
          onChange={handleOnInputChange}
          name="subscriptionNotificationsEnabled"
          label="Subscription notifications enabled"
          description="When disabled, subscribers will not receive email notifications when new jobs are published"
          className=""
          value={subscriptionNotificationsEnabled}
          options={adminBooleanOptions}
          errors={errors}
        />
      </FormContainer>
    </CenterModal>
  );
}

export default AdminCareersPageSubscriptionsModal;

/* Styled Components
======================================================= */
const Styled: any = {};

Styled.ButtonContainer = styled.div((props) => {
  const t: any = props.theme;
  return css`
    label: AdminCareersPageSubscriptionsModal_ButtonContainer;
    display: flex;
    > * {
      ${t.mr(3)}

      &:last-of-type {
        ${t.mr(0)}
      }
    }
  `;
});
